import React, { useState } from 'react';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { conferenceService } from '../../../services/conferenceService';

const CallForPapersSection = ({ callForPapers }) => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('conferences');
  const [downloading, setDownloading] = useState(false);

  const handleDownloadCfp = async () => {
    try {
      setDownloading(true);
      const url = await conferenceService?.getCallForPapersPDFUrl(callForPapers?.pdfUrl);
      if (url) {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Error downloading CFP:', err);
      toastError('Failed to download the document. Please try again.');
    } finally {
      setDownloading(false);
    }
  };
  return (
    <section className="py-4 md:py-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4 md:mb-5">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">{t('conferenceLanding.callForPapers')}</h2>
        </div>

        <div className="prose prose-sm md:prose-base lg:prose-lg max-w-none">
          <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6">
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4">{t('conferenceLanding.overview')}</h3>
              <p className="text-sm md:text-base text-foreground leading-relaxed whitespace-pre-line">
                {callForPapers?.overview}
              </p>
            </div>

            {callForPapers?.topics?.length > 0 && (
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4">{t('conferenceLanding.topicsOfInterest')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                  {callForPapers?.topics?.map((topic, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm md:text-base text-foreground">{topic}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {callForPapers?.guidelines?.length > 0 && (
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 md:mb-4">{t('conferenceLanding.submissionGuidelines')}</h3>
                <div className="space-y-3 md:space-y-4">
                  {callForPapers?.guidelines?.map((guideline, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 md:p-4 bg-muted/50 rounded-lg">
                      <div className="w-6 h-6 bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-medium text-primary">{index + 1}</span>
                      </div>
                      <p className="text-sm md:text-base text-foreground leading-relaxed">{guideline}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {callForPapers?.pdfUrl && (
              <div className="pt-4 md:pt-6 border-t border-border">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Info" size={18} className="text-primary" />
                    <span className="text-xs md:text-sm text-muted-foreground">
                      {t('conferenceLanding.cfpDownloadHint')}
                    </span>
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleDownloadCfp}
                    disabled={downloading}
                    iconName={downloading ? 'Loader' : 'Download'}
                    iconPosition="left"
                    className="flex-shrink-0"
                  >
                    {downloading ? t('conferenceLanding.opening') : t('conferenceLanding.downloadCfpPdf')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallForPapersSection;